import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import apiClient from '../utils/axiosConfig';

const CookieConsent = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [preferencesExpanded, setPreferencesExpanded] = useState(false);
  const [consents, setConsents] = useState({
    necessary: true, // Essential cookies are always enabled
    analytics: false,
    marketing: false,
    personalization: false,
    third_party_sharing: false
  });

  // Check if user has already given consent
  useEffect(() => {
    const consentGiven = localStorage.getItem('cookie_consent');
    if (!consentGiven) {
      setIsVisible(true);
    }
  }, []);

  const handleAcceptAll = async () => {
    const allConsents = {
      necessary: true,
      analytics: true,
      marketing: true,
      personalization: true,
      third_party_sharing: true
    };

    await saveConsent(allConsents, 'all');
    setIsVisible(false);
  };

  const handleDeclineAll = async () => {
    const declinedConsents = {
      necessary: true, // Essential cookies remain enabled
      analytics: false,
      marketing: false,
      personalization: false,
      third_party_sharing: false
    };

    await saveConsent(declinedConsents, 'declined');
    setIsVisible(false);
  };

  const handleSavePreferences = async () => {
    await saveConsent(consents, 'custom');
    setIsVisible(false);
    setPreferencesExpanded(false);
  };

  const handleConsentChange = (purpose, granted) => {
    setConsents(prev => ({
      ...prev,
      [purpose]: granted
    }));
  };

  const saveConsent = async (consentValues, type) => {
    try {
      // Save to localStorage
      localStorage.setItem('cookie_consent', JSON.stringify({
        consent: consentValues,
        timestamp: new Date().toISOString(),
        type: type
      }));

      // If user is logged in, also save to backend
      const userData = localStorage.getItem('customer');
      if (userData) {
        const parsedUserData = JSON.parse(userData);
        const email = parsedUserData.email;

        if (email) {
          // Transform consents to match backend format
          const purposes = Object.keys(consentValues).filter(key => key !== 'necessary').map(key => ({
            purpose: key,
            granted: consentValues[key]
          }));

          await apiClient.post('/consent/update', {
            email,
            purposes
          });

          toast.success('Cookie preferences saved successfully');
        }
      }
    } catch (error) {
      console.error('Error saving consent:', error);
      toast.error('Failed to save cookie preferences');
    }
  };

  if (!isVisible) {
    return null;
  }

  return (
    <div className="fixed bottom-0 left-0 right-0 z-[1001] bg-white shadow-2xl border-t-4 border-red-600 p-4 md:p-6">
      <div className="max-w-6xl mx-auto">
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="flex-1">
            <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-2">We value your privacy</h3>
            <p className="text-sm md:text-base text-gray-700 mb-3">
              We use cookies to enhance your browsing experience, analyze site traffic, and provide personalized content.
              By clicking "Accept All", you consent to our use of cookies.
            </p>
            
            {!preferencesExpanded && (
              <button 
                onClick={() => setPreferencesExpanded(true)}
                className="text-sm text-red-600 hover:text-red-800 font-medium underline"
              >
                Customize preferences
              </button>
            )}
          </div>
          
          <div className="flex flex-col sm:flex-row gap-2 min-w-fit">
            <button
              onClick={handleDeclineAll}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg font-medium hover:bg-gray-200 transition-colors text-sm md:text-base"
            >
              Decline
            </button>
            <button
              onClick={handleAcceptAll}
              className="px-4 py-2 text-white bg-red-600 rounded-lg font-medium hover:bg-red-700 transition-colors text-sm md:text-base"
            >
              Accept All
            </button>
          </div>
        </div>

        {preferencesExpanded && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              {/* Necessary Cookies */}
              <div className="flex items-start p-3 border border-green-200 rounded-lg bg-green-50">
                <div className="flex-1">
                  <div className="flex items-center">
                    <h4 className="font-semibold text-gray-900">Essential Cookies</h4>
                    <span className="ml-2 px-2 py-1 text-xs bg-green-500 text-white rounded">Required</span>
                  </div>
                  <p className="text-sm text-gray-600 mt-1">
                    These cookies are necessary for the website to function and cannot be switched off.
                  </p>
                </div>
                <div className="flex items-center">
                  <span className="px-2 py-1 text-xs bg-green-500 text-white rounded">Always Active</span>
                </div>
              </div>

              {/* Analytics Cookies */}
              <div className="flex items-start p-3 border border-gray-200 rounded-lg hover:border-red-300 transition-colors">
                <div className="flex-1">
                  <div className="flex items-center">
                    <h4 className="font-semibold text-gray-900">Analytics Cookies</h4>
                  </div>
                  <p className="text-sm text-gray-600 mt-1">
                    Allow us to measure visits and traffic to analyze and improve our service.
                  </p>
                </div>
                <div className="flex items-center">
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      className="sr-only peer"
                      checked={consents.analytics}
                      onChange={(e) => handleConsentChange('analytics', e.target.checked)}
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-red-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-red-600"></div>
                  </label>
                </div>
              </div>

              {/* Marketing Cookies */}
              <div className="flex items-start p-3 border border-gray-200 rounded-lg hover:border-red-300 transition-colors">
                <div className="flex-1">
                  <div className="flex items-center">
                    <h4 className="font-semibold text-gray-900">Marketing Cookies</h4>
                  </div>
                  <p className="text-sm text-gray-600 mt-1">
                    Allow us to provide personalized ads based on your interests.
                  </p>
                </div>
                <div className="flex items-center">
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      className="sr-only peer"
                      checked={consents.marketing}
                      onChange={(e) => handleConsentChange('marketing', e.target.checked)}
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-red-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-red-600"></div>
                  </label>
                </div>
              </div>

              {/* Personalization Cookies */}
              <div className="flex items-start p-3 border border-gray-200 rounded-lg hover:border-red-300 transition-colors">
                <div className="flex-1">
                  <div className="flex items-center">
                    <h4 className="font-semibold text-gray-900">Personalization Cookies</h4>
                  </div>
                  <p className="text-sm text-gray-600 mt-1">
                    Allow us to remember your preferences and provide customized content.
                  </p>
                </div>
                <div className="flex items-center">
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      className="sr-only peer"
                      checked={consents.personalization}
                      onChange={(e) => handleConsentChange('personalization', e.target.checked)}
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-red-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-red-600"></div>
                  </label>
                </div>
              </div>

              {/* Third Party Sharing */}
              <div className="flex items-start p-3 border border-gray-200 rounded-lg hover:border-red-300 transition-colors md:col-span-2">
                <div className="flex-1">
                  <div className="flex items-center">
                    <h4 className="font-semibold text-gray-900">Third-Party Sharing</h4>
                  </div>
                  <p className="text-sm text-gray-600 mt-1">
                    Allow us to share your data with trusted partners for marketing purposes.
                  </p>
                </div>
                <div className="flex items-center">
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      className="sr-only peer"
                      checked={consents.third_party_sharing}
                      onChange={(e) => handleConsentChange('third_party_sharing', e.target.checked)}
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-red-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-red-600"></div>
                  </label>
                </div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-2 justify-end">
              <button
                onClick={() => setPreferencesExpanded(false)}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg font-medium hover:bg-gray-200 transition-colors"
              >
                Back
              </button>
              <button
                onClick={handleSavePreferences}
                className="px-4 py-2 text-white bg-red-600 rounded-lg font-medium hover:bg-red-700 transition-colors"
              >
                Save Preferences
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CookieConsent;